/**
 * Refinement Style Store
 * Manages preset and custom refinement prompts
 */

import {
  type CustomStyle,
  MAX_CUSTOM_PROMPTS,
  MAX_PROMPT_LENGTH,
  MIN_PROMPT_LENGTH,
  PRESET_STYLES,
  type RefinementStyle,
} from '@refinevoice/shared';
import { create } from 'zustand';

const STORAGE_KEY_PROMPTS = 'refinevoice_custom_prompts';
const STORAGE_KEY_SELECTED = 'refinevoice_selected_style';

interface StyleStore {
  customStyles: CustomStyle[];
  selectedStyleId: string;

  // Getters
  getAllStyles: () => RefinementStyle[];
  getSelectedStyle: () => RefinementStyle;

  // Actions
  addCustomStyle: (
    name: string,
    prompt: string
  ) => { success: boolean; errorKey?: string; errorParams?: Record<string, any> };
  deleteCustomStyle: (id: string) => void;
  selectStyle: (id: string) => void;

  // Initialization
  loadFromStorage: () => void;
}

export const useStyleStore = create<StyleStore>((set, get) => ({
  customStyles: [],
  selectedStyleId: 'professional', // Default to professional

  getAllStyles: () => {
    const { customStyles } = get();
    return [...PRESET_STYLES, ...customStyles];
  },

  getSelectedStyle: () => {
    const { selectedStyleId } = get();
    const allStyles = get().getAllStyles();
    return allStyles.find((s) => s.id === selectedStyleId) || PRESET_STYLES[0];
  },

  addCustomStyle: (name: string, prompt: string) => {
    const { customStyles } = get();

    // Validate
    if (!name.trim()) {
      return { success: false, errorKey: 'styleErrors.emptyName' };
    }

    if (name.trim().length > 50) {
      return { success: false, errorKey: 'styleErrors.nameTooLong' };
    }

    if (prompt.length < MIN_PROMPT_LENGTH) {
      return {
        success: false,
        errorKey: 'styleErrors.promptTooShort',
        errorParams: { min: MIN_PROMPT_LENGTH },
      };
    }

    if (prompt.length > MAX_PROMPT_LENGTH) {
      return {
        success: false,
        errorKey: 'styleErrors.promptTooLong',
        errorParams: { max: MAX_PROMPT_LENGTH },
      };
    }

    if (customStyles.length >= MAX_CUSTOM_PROMPTS) {
      return {
        success: false,
        errorKey: 'styleErrors.maxPromptsReached',
        errorParams: { max: MAX_CUSTOM_PROMPTS },
      };
    }

    // Check for duplicate name
    if (customStyles.some((s) => s.name.toLowerCase() === name.trim().toLowerCase())) {
      return { success: false, errorKey: 'styleErrors.duplicateName' };
    }

    // Create new custom style
    const newStyle: CustomStyle = {
      id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: name.trim(),
      prompt: prompt.trim(),
      isPreset: false,
      createdAt: Date.now(),
    };

    const updatedCustomStyles = [...customStyles, newStyle];

    set({ customStyles: updatedCustomStyles });

    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY_PROMPTS, JSON.stringify(updatedCustomStyles));
    } catch (error) {
      console.error('Failed to save custom style to localStorage:', error);
    }

    return { success: true };
  },

  deleteCustomStyle: (id: string) => {
    const { customStyles, selectedStyleId } = get();
    const updatedCustomStyles = customStyles.filter((s) => s.id !== id);

    set({ customStyles: updatedCustomStyles });

    // If deleted style was selected, switch to professional
    if (selectedStyleId === id) {
      set({ selectedStyleId: 'professional' });
      try {
        localStorage.setItem(STORAGE_KEY_SELECTED, 'professional');
      } catch (error) {
        console.error('Failed to save selected style:', error);
      }
    }

    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY_PROMPTS, JSON.stringify(updatedCustomStyles));
    } catch (error) {
      console.error('Failed to save custom styles to localStorage:', error);
    }
  },

  selectStyle: (id: string) => {
    set({ selectedStyleId: id });

    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY_SELECTED, id);
    } catch (error) {
      console.error('Failed to save selected style:', error);
    }
  },

  loadFromStorage: () => {
    try {
      // Load custom prompts
      const storedPrompts = localStorage.getItem(STORAGE_KEY_PROMPTS);
      if (storedPrompts) {
        const customStyles = JSON.parse(storedPrompts) as CustomStyle[];
        set({ customStyles });
      }

      // Load selected style
      const storedSelected = localStorage.getItem(STORAGE_KEY_SELECTED);
      if (storedSelected) {
        set({ selectedStyleId: storedSelected });
      }
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
    }
  },
}));

// Load from storage on module initialization
if (typeof window !== 'undefined') {
  useStyleStore.getState().loadFromStorage();
}
